import { type FC, useCallback, useEffect, useState } from 'react';

import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';

import type { AssetsSegmentElasticSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import type { MlPersonByAssetAndVersionNullable } from '@iconik-io/iconik-api/src/services/ml';

import TriggerContextMenu from '../../../components/trigger_context_menu/trigger_context_menu';
import UserBubble from '../../../components/user_bubble/user_bubble';
import { selectIsTimeBasedAsset } from '../../../ducks/pages/asset/selectors';
import { setActivePerson } from '../../../ducks/panels/asset_face_recognition/slice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import Button from '../../../ui/button/button';
import IconButton from '../../../ui/icon_button/icon_button';
import type { MenuItem } from '../../../ui/menus/menu/types';
import { selectUserRoles } from '../../../utils/get_user_roles';
import RecognizedFaceContextMenu from './context_menu';
import { useScrollIntoView } from './hooks/use_scroll_into_view';
import PersonSelect from './person_select';
import { ToggleSegmentButton } from './segment_button';
import SegmentsList from './segments_list';

import styles from './recognized_persons.module.css';

type Props = {
  person: MlPersonByAssetAndVersionNullable;
  segments: AssetsSegmentElasticSchemaWithIdNullable[];
  highlighted: boolean;
  onPersonRowClick: (personId: string) => void;
  newPersonIdInEditMode?: string;
  setNewPersonIdInEditMode?: (personId: string) => void;
  setSelectableItemIds?: (ids: string[]) => void;
};

const NewPerson: FC<Props> = ({
  person,
  segments = [],
  highlighted,
  onPersonRowClick,
  newPersonIdInEditMode,
  setNewPersonIdInEditMode,
  setSelectableItemIds,
}: Props) => {
  const dispatch = useAppDispatch();
  const roles = useAppSelector(selectUserRoles);
  const [isSegmentsExpanded, setIsSegmentsExpanded] = useState(false);
  const [showNewPersonSelect, setShowNewPersonSelect] = useState(false);
  const [actionType, setActionType] = useState<'add' | 'change' | undefined>(
    undefined,
  );
  const newPersonRowRef = useScrollIntoView(highlighted);
  const highlightedSegmentIds = useAppSelector(
    (state) => state.segments.highlightedSegmentIds,
  );

  const { id: assetId, versionId } = useParams({ strict: false });

  const isTimeBasedAsset = useAppSelector((state) =>
    selectIsTimeBasedAsset(state, assetId, versionId),
  );
  const shouldShowSegments = isTimeBasedAsset && segments.length > 0;
  const isSegmentsListVisible =
    isSegmentsExpanded && shouldShowSegments && !showNewPersonSelect;

  const handleMenuItemClick = useCallback((option: MenuItem) => {
    if (option.id === 'change_person') {
      setActionType('change');
      setShowNewPersonSelect(true);
    }
  }, []);

  useEffect(() => {
    if (showNewPersonSelect) {
      setNewPersonIdInEditMode?.(person.id);
    }
  }, [person.id, showNewPersonSelect, setNewPersonIdInEditMode]);

  useEffect(() => {
    if (newPersonIdInEditMode && newPersonIdInEditMode !== person.id) {
      setShowNewPersonSelect(false);
    }
  }, [newPersonIdInEditMode, person.id]);

  return (
    <div
      data-id={person.id}
      ref={newPersonRowRef}
      className={classNames(styles.person_item, styles.new_person)}
      aria-label="Recognized Person - New"
    >
      <div
        className={classNames(styles.person_row, {
          [styles.highlighted]: highlighted,
        })}
        onClick={() => onPersonRowClick(person.id)}
      >
        <div className={styles.person_row_inner}>
          <UserBubble
            className={classNames(
              styles.new_person_image,
              styles.person_avatar,
            )}
            size="md"
            variant="square"
            name={person.name ?? ''}
            src={person.image_url ?? undefined}
          />
          <div
            className={classNames(
              styles.person_row_inner,
              styles.new_person_name_container,
            )}
            onClick={(event) => {
              event.stopPropagation();
              setSelectableItemIds?.([person.id]);
              dispatch(setActivePerson(undefined));
            }}
          >
            {shouldShowSegments ? (
              <ToggleSegmentButton
                isExpanded={isSegmentsExpanded}
                onChange={setIsSegmentsExpanded}
                disabled={showNewPersonSelect}
              >
                {segments.length}
              </ToggleSegmentButton>
            ) : null}
            <Button
              className={styles.add_person_button}
              iconName="loader_circle_done_legacy"
              size="s"
              onClick={() => {
                setShowNewPersonSelect(true);
              }}
              disabled={!roles.canAddPerson || showNewPersonSelect}
            >
              Add person
            </Button>
          </div>
        </div>
        <div
          onClick={(event) => {
            event.stopPropagation();
            dispatch(setActivePerson(undefined));
          }}
        >
          <TriggerContextMenu
            triggerElement={
              <IconButton
                aria-label="Recognized Person Menu"
                iconName="menu_context"
                disabled={showNewPersonSelect}
                onClick={() => {
                  setSelectableItemIds?.([person.id]);
                }}
              />
            }
            contextMenu={RecognizedFaceContextMenu}
            contextItems={[{ ...person }]}
            onMenuItemClick={handleMenuItemClick}
          />
        </div>
      </div>
      {isSegmentsListVisible ? (
        <SegmentsList
          highlightedSegmentIds={highlightedSegmentIds}
          person={person}
          segments={segments}
        />
      ) : null}
      {showNewPersonSelect ? (
        <PersonSelect
          segments={segments}
          onSetShowPersonSelect={setShowNewPersonSelect}
          onDeactivate={() => {
            dispatch(setActivePerson(undefined));
            setSelectableItemIds?.([]);
          }}
          person={person}
          actionType={actionType}
          rowType="new"
        />
      ) : null}
    </div>
  );
};

export default NewPerson;
